<template>
  <Modal
    :show="showModal"
    size="sm"
    @close="closeModal"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
    headerVariant="blue"
  >
    <template #header>
      <div class="bucket-header__text">
        <h2 class="bucket-header__title">Add New Bucket</h2>
        <p class="bucket-header__subtitle">Create a new bucket</p>
      </div>
      <button type="button" class="bucket-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <Form ref="formRef" class="form" @submit="handleFormSubmit">
      <div class="form-group">
        <Field
          id="name"
          :model-value="addBucketOptionObj.name"
          :rules="{ required: ['Name'] }"
          name="name"
          v-slot="{ field, errorMessage }"
        >
          <Input
            :model-value="field.value"
            @update:model-value="
              (value) => {
                field.onChange(value);
                addBucketOptionObj.name = value;
              }
            "
            @blur="field.onBlur"
            placeholder="Enter Bucket Name"
            :error="errorMessage"
            size="sm"
          />
        </Field>
      </div>
      <div class="color-options">
        <div
          v-for="(color, index) in bucketColorOptions"
          :key="index"
          class="color-circle"
          :class="{ 'color-circle--selected': addBucketOptionObj.colorHex === color }"
          :style="{
            backgroundColor: color,
            boxShadow: addBucketOptionObj.colorHex === color ? '0 0 0 6px #E0E0E0' : 'none',
          }"
          @click="
            addBucketOptionObj.colorHex = color;
            colorError = null;
          "
        ></div>
      </div>
      <div v-if="colorError" class="error-message">{{ colorError }}</div>
    </Form>

    <template #footer>
      <div class="modal-footer-buttons">
        <Button
          type="button"
          variant="secondary"
          size="md"
          @click="closeModal"
          :disabled="isLoading"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          @click="handleSaveClick"
          :loading="isLoading"
          :disabled="isLoading"
        >
          Save
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { Modal, Button, Icon, Input } from "@/components/ui";
import { Field, Form } from "vee-validate";
import { useCreateBucketOption } from "@/composables/bucket/useBucketOption";
const props = defineProps({
  selectedTag: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["closeAddBucketOptionModal", "refreshBucketOptions"]);

const $toast = useToast();

// Store
const store = useStore();
const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// Modal state
const showModal = ref(true);
const formRef = ref(null);
const colorError = ref(null);

// Form state
const addBucketOptionObj = ref({
  name: null,
  colorHex: null,
  organizationId: null,
});

// Color options
const bucketColorOptions = [
  "#FA5A7D",
  "#FFD900",
  "#3CD856",
  "#3C3CFA",
  "#00A896",
  "#002B72",
  "#0005FF",
  "#7B003D",
  "#FF002C",
  "#DC00FD",
  "#2D0094",
  "#00AEFD",
  "#00FFAB",
  "#65A700",
];

// Create bucket option mutation
const { mutateAsync: createBucketOptionMutation, isPending: isLoading } = useCreateBucketOption();

// Methods
const setValues = () => {
  addBucketOptionObj.value.organizationId = organizationId.value;
};

const closeModal = () => {
  emit("closeAddBucketOptionModal");
};

const handleSaveClick = () => {
  if (!addBucketOptionObj.value.colorHex) {
    colorError.value = "Please select a color";
    return;
  }
  colorError.value = null;

  if (formRef.value && typeof formRef.value.handleSubmit === "function") {
    const submitHandler = formRef.value.handleSubmit(handleFormSubmit);
    submitHandler();
  } else {
    addBucketOption();
  }
};

const handleFormSubmit = () => {
  addBucketOption();
};

const addBucketOption = async () => {
  if (!addBucketOptionObj.value.colorHex) {
    colorError.value = "Please select a color";
    return;
  }

  try {
    await createBucketOptionMutation(addBucketOptionObj.value);
    $toast?.success("Bucket option created successfully!");
    emit("refreshBucketOptions");
    closeModal();
  } catch (error) {
    console.error("Error creating bucket option:", error);
    $toast?.error(
      error?.response?.data?.msg ||
        error?.message ||
        "Failed to create bucket option. Please try again."
    );
  }
};

// Lifecycle
onMounted(() => {
  setValues();
});
</script>

<style scoped>
.bucket-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bucket-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}

.bucket-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.bucket-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.bucket-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.form {
  display: flex;
  flex-direction: column;
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 6px;
  font-weight: 400;
}

.modal-footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}

.color-options {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 15px;
  padding-left: 12px;
  padding-right: 12px;
}

.color-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.color-circle:hover {
  transform: scale(1.1);
}

.color-circle--selected {
  border: 2px solid #000;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 8px;
  font-weight: 400;
}
</style>
